// Client layer (TS-2.5.03). Presentation container for the Product-grouped LIVE roadmap board
// (decisions.md D-278, revised by D-280): regroups the SAME server-derived board `RoadmapPage`
// renders in Team mode, by joining it against the projects+products read via
// lib/regroupBoardByProduct.ts. THIS COMPONENT NO LONGER FETCHES ITS OWN COPY OF THAT DATA - D-280
// lifted the read into `hooks/useProjectProductJoin.ts`, called once by `RoadmapPage` regardless of
// mode, because the detail cards now need the same join in BOTH modes (D-279's business-owner
// resolution). `RoadmapPage` passes `projects`/`products`/`loading`/`error` down as plain props, so
// this component stays a pure function of them and Team-mode viewers still never pay for a SECOND
// fetch - there is only ever the one, at the page level.
import type { FilterChoice, ProductRecord, ProjectSummary, ProjectView, RoadmapBoard as RoadmapBoardModel } from "../../api/generated/contracts";
import type { ApiError } from "../../api/ApiError";
import { regroupBoardByProduct } from "./lib/regroupBoardByProduct";
import { RoadmapBoard } from "./RoadmapBoard";
import { strings } from "../../strings";

const S = strings.roadmap.groupToggle;

export interface ProductGroupedBoardProps {
  readonly board: RoadmapBoardModel;
  readonly statuses: readonly FilterChoice[];
  readonly selectedStatuses: readonly string[];
  readonly dimmedOpacity: number;
  readonly emptyCellPlaceholder: string;
  readonly projectsByKey: ReadonlyMap<string, ProjectView>;
  readonly projects: readonly ProjectSummary[] | null;
  readonly products: readonly ProductRecord[] | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
}

export function ProductGroupedBoard({
  board,
  statuses,
  selectedStatuses,
  dimmedOpacity,
  emptyCellPlaceholder,
  projectsByKey,
  projects,
  products,
  loading,
  error,
}: ProductGroupedBoardProps) {
  const ready = projects !== null && products !== null;

  if (loading && !ready) {
    return (
      <div role="status" aria-live="polite">
        {S.loading}
      </div>
    );
  }

  if (error && !ready) {
    return (
      <div role="alert">
        <p>{S.errorTitle}</p>
        <p>{error.message}</p>
      </div>
    );
  }

  if (!projects || !products) {
    return null;
  }

  const productBoard = regroupBoardByProduct(board, projects, products, S.unassignedLabel);

  return (
    <RoadmapBoard
      board={productBoard}
      statuses={statuses}
      selectedStatuses={selectedStatuses}
      dimmedOpacity={dimmedOpacity}
      emptyCellPlaceholder={emptyCellPlaceholder}
      projectsByKey={projectsByKey}
    />
  );
}
